import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Plus, Trash2, Clock } from "lucide-react";

const INDUSTRY_IDS = new Set(["restaurant-elegant", "beauty-salon", "fitness-studio", "supermarkt-local"]);

export const hasIndustryEditor = (templateId) => INDUSTRY_IDS.has(templateId);

// ─── Hours-Editor (shared) ─────────────────────────────────────────────
const DAY_KEYS = [
  ["mo", "Montag"], ["di", "Dienstag"], ["mi", "Mittwoch"], ["do", "Donnerstag"],
  ["fr", "Freitag"], ["sa", "Samstag"], ["so", "Sonntag"],
];

const HoursEditor = ({ hours, setHours, sectionNumber, label = "Öffnungszeiten" }) => (
  <section className="space-y-3" data-testid="hours-editor">
    <div className="flex items-center gap-2 pb-2 border-b border-border">
      <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
      <Clock size={13} className="text-muted-foreground" />
      <h3 className="text-sm font-bold uppercase tracking-widest">{label}</h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {DAY_KEYS.map(([key, dayLabel]) => (
        <div key={key} className="flex items-center gap-2">
          <Label className="text-xs font-medium text-muted-foreground w-20 shrink-0">{dayLabel}</Label>
          <Input
            value={hours?.[key] || ""}
            onChange={(e) => setHours({ ...hours, [key]: e.target.value })}
            placeholder="09:00–18:00 oder geschlossen"
            className="rounded-sm text-sm h-8"
            data-testid={`edit-hours-${key}`}
          />
        </div>
      ))}
    </div>
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Hinweis (optional)</Label>
      <Input
        value={hours?.note || ""}
        onChange={(e) => setHours({ ...hours, note: e.target.value })}
        placeholder="z.B. Feiertags geschlossen."
        className="rounded-sm text-sm mt-1"
        data-testid="edit-hours-note"
      />
    </div>
  </section>
);

// ─── 1) Menu-Editor (Restaurant) ───────────────────────────────────────
const MenuEditor = ({ menu, setMenu, sectionNumber }) => {
  const categories = menu?.categories || [];
  const setCat = (i, patch) => {
    const next = categories.map((c, ix) => ix === i ? { ...c, ...patch } : c);
    setMenu({ ...menu, categories: next });
  };
  const addCat = () => setMenu({ ...menu, categories: [...categories, { name: "Neue Kategorie", items: [] }] });
  const removeCat = (i) => setMenu({ ...menu, categories: categories.filter((_, ix) => ix !== i) });
  const setItem = (ci, ii, patch) => {
    const items = (categories[ci].items || []).map((it, ix) => ix === ii ? { ...it, ...patch } : it);
    setCat(ci, { items });
  };
  const addItem = (ci) => setCat(ci, { items: [...(categories[ci].items || []), { name: "", description: "", price: "" }] });
  const removeItem = (ci, ii) => setCat(ci, { items: (categories[ci].items || []).filter((_, ix) => ix !== ii) });

  return (
    <section className="space-y-3" data-testid="menu-editor">
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
          <h3 className="text-sm font-bold uppercase tracking-widest">Speisekarte</h3>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={addCat} className="h-7 px-2 text-xs rounded-sm gap-1" data-testid="add-menu-category-btn">
          <Plus size={11} /> Kategorie
        </Button>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Einleitungstext</Label>
        <Textarea
          value={menu?.intro || ""}
          onChange={(e) => setMenu({ ...menu, intro: e.target.value })}
          rows={2}
          className="rounded-sm text-sm mt-1"
          data-testid="edit-menu-intro"
          placeholder="z.B. Alle Preise inkl. MwSt."
        />
      </div>
      {categories.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Noch keine Kategorien. Füge z.B. „Vorspeisen", „Hauptgerichte", „Desserts" hinzu.</p>
      )}
      {categories.map((cat, ci) => (
        <div key={ci} className="p-3 bg-muted/30 rounded-sm space-y-2 border border-border">
          <div className="flex items-center gap-2">
            <Input
              value={cat.name || ""}
              onChange={(e) => setCat(ci, { name: e.target.value })}
              placeholder="Kategorie-Name"
              className="rounded-sm text-sm font-semibold h-8"
              data-testid={`edit-menu-cat-name-${ci}`}
            />
            <button type="button" onClick={() => removeCat(ci)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-menu-cat-${ci}`}>
              <Trash2 size={12} />
            </button>
          </div>
          <div className="space-y-1.5 pl-2 border-l-2 border-border">
            {(cat.items || []).map((it, ii) => (
              <div key={ii} className="flex items-start gap-1.5">
                <div className="flex-1 space-y-1">
                  <div className="flex gap-1.5">
                    <Input value={it.name || ""} onChange={(e) => setItem(ci, ii, { name: e.target.value })} placeholder="Gericht" className="rounded-sm text-sm h-8" data-testid={`edit-menu-item-name-${ci}-${ii}`} />
                    <Input value={it.price || ""} onChange={(e) => setItem(ci, ii, { price: e.target.value })} placeholder="12,90 €" className="rounded-sm text-sm h-8 w-24" data-testid={`edit-menu-item-price-${ci}-${ii}`} />
                  </div>
                  <Input value={it.description || ""} onChange={(e) => setItem(ci, ii, { description: e.target.value })} placeholder="Beschreibung" className="rounded-sm text-xs h-7" data-testid={`edit-menu-item-desc-${ci}-${ii}`} />
                </div>
                <button type="button" onClick={() => removeItem(ci, ii)} className="text-muted-foreground hover:text-destructive p-1 mt-1" data-testid={`remove-menu-item-${ci}-${ii}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            <Button type="button" size="sm" variant="ghost" onClick={() => addItem(ci)} className="h-6 px-2 text-xs rounded-sm gap-1" data-testid={`add-menu-item-${ci}`}>
              <Plus size={10} /> Gericht
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
};

// ─── 2) Pricelist-Editor (Beauty) ───────────────────────────────────────
const PricelistEditor = ({ pricelist, setPricelist, sectionNumber }) => {
  const categories = pricelist?.categories || [];
  const setCat = (i, patch) => setPricelist({ ...pricelist, categories: categories.map((c, ix) => ix === i ? { ...c, ...patch } : c) });
  const addCat = () => setPricelist({ ...pricelist, categories: [...categories, { name: "Neue Kategorie", items: [] }] });
  const removeCat = (i) => setPricelist({ ...pricelist, categories: categories.filter((_, ix) => ix !== i) });
  const setItem = (ci, ii, patch) => setCat(ci, { items: (categories[ci].items || []).map((it, ix) => ix === ii ? { ...it, ...patch } : it) });
  const addItem = (ci) => setCat(ci, { items: [...(categories[ci].items || []), { service: "", duration: "", price: "" }] });
  const removeItem = (ci, ii) => setCat(ci, { items: (categories[ci].items || []).filter((_, ix) => ix !== ii) });

  return (
    <section className="space-y-3" data-testid="pricelist-editor">
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
          <h3 className="text-sm font-bold uppercase tracking-widest">Preisliste</h3>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={addCat} className="h-7 px-2 text-xs rounded-sm gap-1" data-testid="add-price-category-btn">
          <Plus size={11} /> Kategorie
        </Button>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Einleitungstext</Label>
        <Textarea value={pricelist?.intro || ""} onChange={(e) => setPricelist({ ...pricelist, intro: e.target.value })} rows={2} className="rounded-sm text-sm mt-1" data-testid="edit-pricelist-intro" />
      </div>
      {categories.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Noch keine Kategorien. Füge z.B. „Damen", „Herren", „Pflege" hinzu.</p>
      )}
      {categories.map((cat, ci) => (
        <div key={ci} className="p-3 bg-muted/30 rounded-sm space-y-2 border border-border">
          <div className="flex items-center gap-2">
            <Input value={cat.name || ""} onChange={(e) => setCat(ci, { name: e.target.value })} placeholder="Kategorie-Name" className="rounded-sm text-sm font-semibold h-8" data-testid={`edit-price-cat-name-${ci}`} />
            <button type="button" onClick={() => removeCat(ci)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-price-cat-${ci}`}>
              <Trash2 size={12} />
            </button>
          </div>
          <div className="space-y-1.5 pl-2 border-l-2 border-border">
            {(cat.items || []).map((it, ii) => (
              <div key={ii} className="flex items-start gap-1.5">
                <div className="flex-1 space-y-1">
                  <Input value={it.service || ""} onChange={(e) => setItem(ci, ii, { service: e.target.value })} placeholder="Dienstleistung" className="rounded-sm text-sm h-8" data-testid={`edit-price-item-service-${ci}-${ii}`} />
                  <div className="flex gap-1.5">
                    <Input value={it.duration || ""} onChange={(e) => setItem(ci, ii, { duration: e.target.value })} placeholder="ca. 60 min" className="rounded-sm text-xs h-7" data-testid={`edit-price-item-duration-${ci}-${ii}`} />
                    <Input value={it.price || ""} onChange={(e) => setItem(ci, ii, { price: e.target.value })} placeholder="ab 55 €" className="rounded-sm text-xs h-7" data-testid={`edit-price-item-price-${ci}-${ii}`} />
                  </div>
                </div>
                <button type="button" onClick={() => removeItem(ci, ii)} className="text-muted-foreground hover:text-destructive p-1 mt-1" data-testid={`remove-price-item-${ci}-${ii}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            <Button type="button" size="sm" variant="ghost" onClick={() => addItem(ci)} className="h-6 px-2 text-xs rounded-sm gap-1" data-testid={`add-price-item-${ci}`}>
              <Plus size={10} /> Leistung
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
};

// ─── 3) Schedule-Editor (Fitness) ───────────────────────────────────────
const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const ScheduleEditor = ({ schedule, setSchedule, sectionNumber }) => {
  const classes = schedule?.classes || [];
  const setClass = (i, patch) => setSchedule({ ...schedule, classes: classes.map((c, ix) => ix === i ? { ...c, ...patch } : c) });
  const addClass = () => setSchedule({ ...schedule, classes: [...classes, { day: "Mo", time: "18:00", name: "", trainer: "" }] });
  const removeClass = (i) => setSchedule({ ...schedule, classes: classes.filter((_, ix) => ix !== i) });

  return (
    <section className="space-y-3" data-testid="schedule-editor">
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
          <h3 className="text-sm font-bold uppercase tracking-widest">Kursplan</h3>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={addClass} className="h-7 px-2 text-xs rounded-sm gap-1" data-testid="add-schedule-class-btn">
          <Plus size={11} /> Kurs
        </Button>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Hinweis</Label>
        <Input value={schedule?.note || ""} onChange={(e) => setSchedule({ ...schedule, note: e.target.value })} placeholder="z.B. Alle Kurse in der Mitgliedschaft enthalten." className="rounded-sm text-sm mt-1" data-testid="edit-schedule-note" />
      </div>
      {classes.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Noch keine Kurse. Klicke auf „Kurs".</p>
      )}
      {classes.map((c, i) => (
        <div key={i} className="grid grid-cols-[60px_80px_1fr_1fr_auto] gap-1.5 items-center">
          <select
            value={c.day || "Mo"}
            onChange={(e) => setClass(i, { day: e.target.value })}
            className="rounded-sm text-sm h-8 border border-input bg-background px-2"
            data-testid={`edit-schedule-day-${i}`}
          >
            {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <Input value={c.time || ""} onChange={(e) => setClass(i, { time: e.target.value })} placeholder="18:00" className="rounded-sm text-sm h-8" data-testid={`edit-schedule-time-${i}`} />
          <Input value={c.name || ""} onChange={(e) => setClass(i, { name: e.target.value })} placeholder="Kurs-Name" className="rounded-sm text-sm h-8" data-testid={`edit-schedule-name-${i}`} />
          <Input value={c.trainer || ""} onChange={(e) => setClass(i, { trainer: e.target.value })} placeholder="Trainer" className="rounded-sm text-sm h-8" data-testid={`edit-schedule-trainer-${i}`} />
          <button type="button" onClick={() => removeClass(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-schedule-class-${i}`}>
            <Trash2 size={11} />
          </button>
        </div>
      ))}
    </section>
  );
};

// ─── 4) Memberships-Editor (Fitness) ────────────────────────────────────
const MembershipsEditor = ({ memberships, setMemberships, sectionNumber }) => {
  const tiers = memberships?.tiers || [];
  const setTier = (i, patch) => setMemberships({ ...memberships, tiers: tiers.map((t, ix) => ix === i ? { ...t, ...patch } : t) });
  const addTier = () => {
    if (tiers.length >= 4) return;
    setMemberships({ ...memberships, tiers: [...tiers, { name: "Neuer Tarif", price: "", period: "/Monat", features: [] }] });
  };
  const removeTier = (i) => setMemberships({ ...memberships, tiers: tiers.filter((_, ix) => ix !== i) });
  const setFeature = (ti, fi, v) => setTier(ti, { features: (tiers[ti].features || []).map((f, ix) => ix === fi ? v : f) });
  const addFeature = (ti) => setTier(ti, { features: [...(tiers[ti].features || []), ""] });
  const removeFeature = (ti, fi) => setTier(ti, { features: (tiers[ti].features || []).filter((_, ix) => ix !== fi) });

  return (
    <section className="space-y-3" data-testid="memberships-editor">
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
          <h3 className="text-sm font-bold uppercase tracking-widest">Mitgliedschaften</h3>
        </div>
        {tiers.length < 4 && (
          <Button type="button" size="sm" variant="ghost" onClick={addTier} className="h-7 px-2 text-xs rounded-sm gap-1" data-testid="add-tier-btn">
            <Plus size={11} /> Tarif
          </Button>
        )}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Hinweis</Label>
        <Input value={memberships?.note || ""} onChange={(e) => setMemberships({ ...memberships, note: e.target.value })} placeholder="z.B. Alle Preise brutto." className="rounded-sm text-sm mt-1" data-testid="edit-memberships-note" />
      </div>
      {tiers.map((t, i) => (
        <div key={i} className="p-3 bg-muted/30 rounded-sm space-y-2 border border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Tarif {i + 1}</span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={!!t.highlight} onChange={(e) => setTier(i, { highlight: e.target.checked })} data-testid={`edit-tier-highlight-${i}`} />
                Hervorheben
              </label>
              <button type="button" onClick={() => removeTier(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-tier-${i}`}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <Input value={t.name || ""} onChange={(e) => setTier(i, { name: e.target.value })} placeholder="Flex" className="rounded-sm text-sm h-8" data-testid={`edit-tier-name-${i}`} />
            <Input value={t.price || ""} onChange={(e) => setTier(i, { price: e.target.value })} placeholder="39€" className="rounded-sm text-sm h-8" data-testid={`edit-tier-price-${i}`} />
            <Input value={t.period || ""} onChange={(e) => setTier(i, { period: e.target.value })} placeholder="/Monat" className="rounded-sm text-sm h-8" data-testid={`edit-tier-period-${i}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Features</Label>
            {(t.features || []).map((f, fi) => (
              <div key={fi} className="flex gap-1.5">
                <Input value={f} onChange={(e) => setFeature(i, fi, e.target.value)} placeholder="Feature" className="rounded-sm text-xs h-7" data-testid={`edit-tier-feature-${i}-${fi}`} />
                <button type="button" onClick={() => removeFeature(i, fi)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-tier-feature-${i}-${fi}`}>
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
            <Button type="button" size="sm" variant="ghost" onClick={() => addFeature(i)} className="h-6 px-2 text-xs rounded-sm gap-1" data-testid={`add-tier-feature-${i}`}>
              <Plus size={10} /> Feature
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
};

// ─── 5) Offers-Editor (Supermarkt) ──────────────────────────────────────
const OffersEditor = ({ offers, setOffers, sectionNumber }) => {
  const items = offers?.items || [];
  const setItem = (i, patch) => setOffers({ ...offers, items: items.map((it, ix) => ix === i ? { ...it, ...patch } : it) });
  const addItem = () => {
    if (items.length >= 6) return;
    setOffers({ ...offers, items: [...items, { name: "", description: "", price: "", unit: "" }] });
  };
  const removeItem = (i) => setOffers({ ...offers, items: items.filter((_, ix) => ix !== i) });

  return (
    <section className="space-y-3" data-testid="offers-editor">
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
          <h3 className="text-sm font-bold uppercase tracking-widest">Wochenangebote</h3>
        </div>
        {items.length < 6 && (
          <Button type="button" size="sm" variant="ghost" onClick={addItem} className="h-7 px-2 text-xs rounded-sm gap-1" data-testid="add-offer-btn">
            <Plus size={11} /> Angebot
          </Button>
        )}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Hinweis</Label>
        <Input value={offers?.note || ""} onChange={(e) => setOffers({ ...offers, note: e.target.value })} placeholder="z.B. Gültig solange der Vorrat reicht." className="rounded-sm text-sm mt-1" data-testid="edit-offers-note" />
      </div>
      {items.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Noch keine Angebote. Maximal 6 möglich.</p>
      )}
      {items.map((it, i) => (
        <div key={i} className="p-3 bg-muted/30 rounded-sm space-y-2 border border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Angebot {i + 1}</span>
            <button type="button" onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-offer-${i}`}>
              <Trash2 size={12} />
            </button>
          </div>
          <Input value={it.name || ""} onChange={(e) => setItem(i, { name: e.target.value })} placeholder="Produktname" className="rounded-sm text-sm h-8" data-testid={`edit-offer-name-${i}`} />
          <Input value={it.description || ""} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="Beschreibung / Herkunft" className="rounded-sm text-xs h-7" data-testid={`edit-offer-desc-${i}`} />
          <div className="grid grid-cols-2 gap-1.5">
            <Input value={it.price || ""} onChange={(e) => setItem(i, { price: e.target.value })} placeholder="2,99€" className="rounded-sm text-sm h-8" data-testid={`edit-offer-price-${i}`} />
            <Input value={it.unit || ""} onChange={(e) => setItem(i, { unit: e.target.value })} placeholder="je kg" className="rounded-sm text-sm h-8" data-testid={`edit-offer-unit-${i}`} />
          </div>
        </div>
      ))}
    </section>
  );
};

// ─── 6) Store-Services-Editor (Supermarkt) ──────────────────────────────
const StoreServicesEditor = ({ storeServices, setStoreServices, sectionNumber }) => {
  const items = storeServices?.items || [];
  const setItem = (i, patch) => setStoreServices({ ...storeServices, items: items.map((it, ix) => ix === i ? { ...it, ...patch } : it) });
  const addItem = () => {
    if (items.length >= 4) return;
    setStoreServices({ ...storeServices, items: [...items, { title: "", description: "" }] });
  };
  const removeItem = (i) => setStoreServices({ ...storeServices, items: items.filter((_, ix) => ix !== i) });

  return (
    <section className="space-y-3" data-testid="store-services-editor">
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
          <h3 className="text-sm font-bold uppercase tracking-widest">Services (Markt-Vorteile)</h3>
        </div>
        {items.length < 4 && (
          <Button type="button" size="sm" variant="ghost" onClick={addItem} className="h-7 px-2 text-xs rounded-sm gap-1" data-testid="add-store-service-btn">
            <Plus size={11} /> Service
          </Button>
        )}
      </div>
      {items.map((it, i) => (
        <div key={i} className="p-3 bg-muted/30 rounded-sm space-y-2 border border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Service {i + 1}</span>
            <button type="button" onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-store-service-${i}`}>
              <Trash2 size={12} />
            </button>
          </div>
          <Input value={it.title || ""} onChange={(e) => setItem(i, { title: e.target.value })} placeholder="z.B. Kostenlose Parkplätze" className="rounded-sm text-sm h-8" data-testid={`edit-store-service-title-${i}`} />
          <Textarea value={it.description || ""} onChange={(e) => setItem(i, { description: e.target.value })} rows={2} placeholder="Beschreibung" className="rounded-sm text-xs" data-testid={`edit-store-service-desc-${i}`} />
        </div>
      ))}
    </section>
  );
};

// ─── Main Industry-Editor (router) ──────────────────────────────────────
export const IndustryEditor = ({ templateId, editContent, setEditContent, startSection = 7 }) => {
  if (!hasIndustryEditor(templateId) || !editContent) return null;

  const setField = (field, value) => setEditContent((prev) => ({ ...prev, [field]: value }));

  if (templateId === "restaurant-elegant") {
    return (
      <>
        <MenuEditor menu={editContent.menu || {}} setMenu={(v) => setField("menu", v)} sectionNumber={startSection} />
        <HoursEditor hours={editContent.hours || {}} setHours={(v) => setField("hours", v)} sectionNumber={startSection + 1} />
      </>
    );
  }
  if (templateId === "beauty-salon") {
    return (
      <>
        <PricelistEditor pricelist={editContent.pricelist || {}} setPricelist={(v) => setField("pricelist", v)} sectionNumber={startSection} />
        <HoursEditor hours={editContent.hours || {}} setHours={(v) => setField("hours", v)} sectionNumber={startSection + 1} />
      </>
    );
  }
  if (templateId === "fitness-studio") {
    return (
      <>
        <ScheduleEditor schedule={editContent.schedule || {}} setSchedule={(v) => setField("schedule", v)} sectionNumber={startSection} />
        <MembershipsEditor memberships={editContent.memberships || {}} setMemberships={(v) => setField("memberships", v)} sectionNumber={startSection + 1} />
      </>
    );
  }
  if (templateId === "supermarkt-local") {
    return (
      <>
        <OffersEditor offers={editContent.offers || {}} setOffers={(v) => setField("offers", v)} sectionNumber={startSection} />
        <StoreServicesEditor storeServices={editContent.store_services || {}} setStoreServices={(v) => setField("store_services", v)} sectionNumber={startSection + 1} />
        <HoursEditor hours={editContent.hours || {}} setHours={(v) => setField("hours", v)} sectionNumber={startSection + 2} />
      </>
    );
  }
  return null;
};
